/**
 * CSV の 1 行（`string[]`）を「列名をキーに持つオブジェクト」へ変換する共通ヘルパー。
 *
 * Menu / Corner / Special の各バリデータが同じ処理を個別に持っていたため集約した（Refs #556）。
 *
 * ## なぜ `Record<string, string>` を使わないか
 * `tsconfig.json` の `noUncheckedIndexedAccess` 下では `Record<string, string>` の
 * プロパティアクセスが `string | undefined` になる。各バリデータは `d.data_type` の
 * ように数十箇所でアクセスするため、そのすべてに `?? ''` を書くと
 *
 *   - 実行時には決して通らない分岐が数十個増える（カバレッジ 100% を維持できない）
 *   - 列名の打ち間違い（`d.start_ad`）が `undefined` として静かに通る
 *
 * という二重の問題が起きる。列名タプルからキーのユニオン型を導出して
 * `Record<'data_type' | 'menu_id' | ..., string>` にすることで、
 * アクセスは `string` 確定・未定義の列名はコンパイルエラーになる。
 */

/** 列名タプル `C` に対応する「列名 → 値」の型。キーが確定するため値は `string`（`| undefined` にならない）。 */
export type CsvRow<C extends readonly string[]> = Record<C[number], string>;

/**
 * `columns` の順に `row` の値を割り当てたオブジェクトを返す（値は trim 済み）。
 *
 * `row` が `columns` より短い場合、足りない列は空文字列になる。
 * 呼び出し元は列数一致を検証済みだが、本関数単体でも欠損に対して安全に振る舞う。
 */
export const toCsvRow = <C extends readonly string[]>(
  columns: C,
  row: readonly string[],
): CsvRow<C> => {
  // ⚠ AGENTS.md 1（キャスト原則禁止）の**例外**。1-1 の手続きに従い理由を明記する。
  // columns がキーの全体集合そのものなので、ループ完了後に Record<C[number], string> が
  // 満たされることは構造上保証されるが、**TypeScript はそれを証明できない**
  // （`Object.fromEntries` の戻り値型は `{[k: string]: string}` で、ジェネリックな
  //  `Record<C[number], string>` へは代入できない）。型ガードでも表現できないため、
  // この 1 箇所だけアサーションで組み立て、外部にはキャストを漏らさない。
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 上記理由（TS が証明できない構造）
  const result = {} as Record<C[number], string>;
  columns.forEach((column, index) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上（columns の要素はキーの全体集合）
    result[column as C[number]] = (row[index] ?? '').trim();
  });
  return result;
};
